import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RotateCcw, Search } from 'lucide-react'

import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent } from '@quebon/ui/components/card'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@quebon/ui/components/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'

import { adminApi } from '@/api/admin'
import {
  LC_LEDGER_CHANNEL_LABELS,
  LC_LEDGER_CHANNELS,
  LC_LEDGER_ENTRY_TYPE_LABELS,
  LC_LEDGER_ENTRY_TYPES,
  type LcLedgerChannel,
  type LcLedgerEntryType,
} from '@/api/lcSettlement'
import { EntriesTab } from './EntriesTab'
import { SummaryTab } from './SummaryTab'
import { maxDateTo, useLcSettlementFilters } from './useLcSettlementFilters'

const ALL = 'all'
const LEARNING_CENTER_TYPE = 'learning_center'

// base-ui Select renders the raw value in the trigger unless `items` maps values to labels.
const CHANNEL_ITEMS = [
  { value: ALL, label: '전체 채널' },
  ...LC_LEDGER_CHANNELS.map((channel) => ({ value: channel, label: LC_LEDGER_CHANNEL_LABELS[channel] })),
]
const ENTRY_TYPE_ITEMS = [
  { value: ALL, label: '결제 + 환불' },
  ...LC_LEDGER_ENTRY_TYPES.map((entryType) => ({ value: entryType, label: LC_LEDGER_ENTRY_TYPE_LABELS[entryType] })),
]

type LedgerTab = 'summary' | 'entries'

export function LcSettlementPage() {
  const { draft, setDraft, params, error, apply, reset } = useLcSettlementFilters()
  const [tab, setTab] = useState<LedgerTab>('summary')

  const centersQuery = useQuery({
    queryKey: ['admin', 'organizations', 'learning-centers'],
    queryFn: () => adminApi.getOrganizations(),
    select: (organizations) =>
      organizations
        .filter((organization) => organization.organizationType === LEARNING_CENTER_TYPE)
        .sort((a, b) => a.name.localeCompare(b.name, 'ko')),
    staleTime: 5 * 60 * 1000,
  })
  const centers = useMemo(() => centersQuery.data ?? [], [centersQuery.data])
  const centerItems = useMemo(
    () => [
      { value: ALL, label: '전체 센터' },
      ...centers.map((center) => ({
        value: String(center.id),
        label: `${center.name}${center.lcOperationMode === 'TEST' ? ' (테스트센터)' : ''}`,
      })),
    ],
    [centers],
  )

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    apply()
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-bold text-foreground">LC 정산</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          러닝센터가 깨봉에 지불한 콘텐츠 사용료와 깨봉몰 주문을 기간별로 조회하고 CSV로 내려받습니다. 수수료·정산금은 계산하지 않는 읽기 전용 결제 대장입니다.
        </p>
      </section>

      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <FieldGroup className="grid items-end gap-4 lg:grid-cols-[1fr_1fr_1.4fr_1fr_1fr]">
              <Field>
                <FieldLabel htmlFor="lc-settlement-from">시작일</FieldLabel>
                <Input
                  id="lc-settlement-from"
                  type="date"
                  value={draft.dateFrom}
                  max={draft.dateTo || undefined}
                  onChange={(event) => setDraft({ ...draft, dateFrom: event.target.value })}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="lc-settlement-to">종료일</FieldLabel>
                <Input
                  id="lc-settlement-to"
                  type="date"
                  value={draft.dateTo}
                  min={draft.dateFrom || undefined}
                  max={maxDateTo(draft.dateFrom)}
                  onChange={(event) => setDraft({ ...draft, dateTo: event.target.value })}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="lc-settlement-center">센터</FieldLabel>
                <Select
                  items={centerItems}
                  value={draft.organizationId || ALL}
                  onValueChange={(value) => setDraft({ ...draft, organizationId: !value || value === ALL ? '' : value })}
                >
                  <SelectTrigger id="lc-settlement-center" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {centerItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="lc-settlement-channel">채널</FieldLabel>
                <Select
                  items={CHANNEL_ITEMS}
                  value={draft.channel || ALL}
                  onValueChange={(value) =>
                    setDraft({ ...draft, channel: !value || value === ALL ? '' : (value as LcLedgerChannel) })}
                >
                  <SelectTrigger id="lc-settlement-channel" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {CHANNEL_ITEMS.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="lc-settlement-entry-type">구분</FieldLabel>
                <Select
                  items={ENTRY_TYPE_ITEMS}
                  value={draft.entryType || ALL}
                  onValueChange={(value) =>
                    setDraft({ ...draft, entryType: !value || value === ALL ? '' : (value as LcLedgerEntryType) })}
                >
                  <SelectTrigger id="lc-settlement-entry-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {ENTRY_TYPE_ITEMS.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            </FieldGroup>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label htmlFor="lc-settlement-include-test" className="flex items-center gap-2 text-sm text-foreground">
                <Checkbox
                  id="lc-settlement-include-test"
                  checked={draft.includeTest}
                  onCheckedChange={(checked) => setDraft({ ...draft, includeTest: checked === true })}
                />
                테스트 센터 포함
              </label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={reset}>
                  <RotateCcw data-icon="inline-start" />
                  초기화
                </Button>
                <Button type="submit">
                  <Search data-icon="inline-start" />
                  조회
                </Button>
              </div>
            </div>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </form>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(value) => setTab(value as LedgerTab)}>
        <TabsList variant="line">
          <TabsTrigger value="summary">요약</TabsTrigger>
          <TabsTrigger value="entries">상세</TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="pt-4">
          <SummaryTab params={params} />
        </TabsContent>
        <TabsContent value="entries" className="pt-4">
          <EntriesTab params={params} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
